import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Database,
  Target,
  Layers,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
  FileJson,
  FolderOpen,
  Image,
  CheckCircle2,
  AlertCircle,
  FileCode,
  Lightbulb,
  FolderTree,
  Tag,
  ImageIcon,
} from 'lucide-react';
import {
  TAO6Dataset,
  TAO6TaskType,
  TAO6DatasetFormat,
} from '@/mock/gpu-instance';

interface TAO6DatasetCardProps {
  dataset: TAO6Dataset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectDataset?: (dataset: TAO6Dataset) => void;
}

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-5 w-5 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-5 w-5 text-blue-500" />;
    case 'instance_segmentation':
      return <Box className="h-5 w-5 text-purple-500" />;
    case 'semantic_segmentation':
      return <Shapes className="h-5 w-5 text-cyan-500" />;
    case 'pose_estimation':
      return <PersonStanding className="h-5 w-5 text-orange-500" />;
    case 'action_recognition':
      return <Video className="h-5 w-5 text-pink-500" />;
    case 'ocr':
      return <ScanText className="h-5 w-5 text-amber-500" />;
  }
};

const getTaskBadge = (task: TAO6TaskType) => {
  const config: Record<TAO6TaskType, { bg: string; text: string; label: string }> = {
    object_detection: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Object Detection' },
    image_classification: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Image Classification' },
    instance_segmentation: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Instance Segmentation' },
    semantic_segmentation: { bg: 'bg-cyan-500/10', text: 'text-cyan-600', label: 'Semantic Segmentation' },
    pose_estimation: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'Pose Estimation' },
    action_recognition: { bg: 'bg-pink-500/10', text: 'text-pink-600', label: 'Action Recognition' },
    ocr: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'OCR' },
  };
  const { bg, text, label } = config[task];
  return (
    <Badge className={`${bg} ${text} border-current/30`}>
      {getTaskIcon(task)}
      <span className="ml-1">{label}</span>
    </Badge>
  );
};

const getFormatIcon = (format: TAO6DatasetFormat) => {
  switch (format) {
    case 'coco':
      return <FileJson className="h-4 w-4 text-blue-500" />;
    case 'kitti':
      return <FolderOpen className="h-4 w-4 text-purple-500" />;
    case 'directory':
      return <Image className="h-4 w-4 text-green-500" />;
  }
};

// Get annotation example based on task and format
const getAnnotationExample = (task: TAO6TaskType, format: TAO6DatasetFormat) => {
  if (format === 'coco') {
    switch (task) {
      case 'object_detection':
        return `{
  "images": [
    {"id": 1, "file_name": "image_001.jpg", "width": 1920, "height": 1080}
  ],
  "annotations": [
    {
      "id": 1,
      "image_id": 1,
      "category_id": 1,
      "bbox": [100, 200, 150, 300],  // [x, y, width, height]
      "area": 45000,
      "iscrowd": 0
    }
  ],
  "categories": [
    {"id": 1, "name": "person"},
    {"id": 2, "name": "car"}
  ]
}`;
      case 'instance_segmentation':
        return `{
  "images": [
    {"id": 1, "file_name": "image_001.jpg", "width": 1920, "height": 1080}
  ],
  "annotations": [
    {
      "id": 1,
      "image_id": 1,
      "category_id": 1,
      "bbox": [100, 200, 150, 300],
      "segmentation": [[110, 210, 120, 220, 130, 210, ...]],  // polygon points
      "area": 45000,
      "iscrowd": 0
    }
  ],
  "categories": [
    {"id": 1, "name": "cell_nucleus"}
  ]
}`;
      case 'semantic_segmentation':
        return `{
  "images": [
    {"id": 1, "file_name": "image_001.jpg", "width": 1920, "height": 1080}
  ],
  "annotations": [
    {
      "id": 1,
      "image_id": 1,
      "segmentation": "mask_001.png",  // PNG mask file
      "category_ids": [1, 2, 3, 5, 7]  // classes present in image
    }
  ],
  "categories": [
    {"id": 1, "name": "road", "color": [128, 64, 128]},
    {"id": 2, "name": "sidewalk", "color": [244, 35, 232]}
  ]
}`;
      case 'pose_estimation':
        return `{
  "images": [
    {"id": 1, "file_name": "image_001.jpg", "width": 1920, "height": 1080}
  ],
  "annotations": [
    {
      "id": 1,
      "image_id": 1,
      "category_id": 1,
      "keypoints": [
        430, 260, 2,  // nose: x, y, visibility
        445, 245, 2,  // left_eye
        415, 245, 2,  // right_eye
        ...
      ],
      "num_keypoints": 17,
      "bbox": [380, 200, 150, 400]
    }
  ],
  "categories": [
    {
      "id": 1,
      "name": "person",
      "keypoints": ["nose", "left_eye", "right_eye", ...]
    }
  ]
}`;
      case 'ocr':
        return `{
  "images": [
    {"id": 1, "file_name": "image_001.jpg", "width": 1920, "height": 1080}
  ],
  "annotations": [
    {
      "id": 1,
      "image_id": 1,
      "category_id": 1,
      "bbox": [100, 200, 300, 50],
      "text": "HELLO WORLD",
      "polygon": [100, 200, 400, 200, 400, 250, 100, 250]
    }
  ],
  "categories": [
    {"id": 1, "name": "text_region"}
  ]
}`;
      default:
        return `{
  "images": [...],
  "annotations": [...],
  "categories": [...]
}`;
    }
  } else if (format === 'kitti') {
    return `# KITTI format label file (image_001.txt)
# class truncated occluded alpha bbox(x1,y1,x2,y2) dimensions(h,w,l) location(x,y,z) rotation_y

Car 0.00 0 -1.57 614.24 181.78 727.31 284.77 1.57 1.73 4.15 1.00 1.75 13.22 -1.57
Pedestrian 0.00 0 0.21 423.17 173.67 433.17 224.03 1.67 0.47 0.34 -3.45 1.65 15.14 -1.35
Cyclist 0.00 1 -2.46 712.40 143.00 810.73 307.92 1.89 0.48 1.20 0.35 1.84 8.41 -2.69`;
  } else {
    // directory format
    switch (task) {
      case 'image_classification':
        return `# Directory structure for classification
dataset/
├── train/
│   ├── class_1/
│   │   ├── image_001.jpg
│   │   ├── image_002.jpg
│   │   └── ...
│   ├── class_2/
│   │   ├── image_001.jpg
│   │   └── ...
│   └── class_n/
│       └── ...
├── val/
│   ├── class_1/
│   ├── class_2/
│   └── ...
└── test/
    └── ...`;
      case 'action_recognition':
        return `# Directory structure for action recognition
dataset/
├── train/
│   ├── action_1/
│   │   ├── video_001.mp4
│   │   ├── video_002.mp4
│   │   └── ...
│   ├── action_2/
│   │   └── ...
│   └── action_n/
│       └── ...
├── val/
│   └── ...
└── annotations.json  # optional metadata`;
      default:
        return `# Directory structure
dataset/
├── train/
│   ├── images/
│   └── labels/
├── val/
│   ├── images/
│   └── labels/
└── test/
    └── ...`;
    }
  }
};

// Get labeling tips based on task
const getLabelingTips = (task: TAO6TaskType): string[] => {
  switch (task) {
    case 'object_detection':
      return [
        'Draw tight bounding boxes around each object instance',
        'Include partially visible objects (mark as occluded if needed)',
        'Be consistent with class naming across all images',
        'Ensure boxes don\'t extend beyond image boundaries',
        'Label all instances of each class, even small ones',
      ];
    case 'image_classification':
      return [
        'Each image should contain only one primary class',
        'Ensure images are clearly representative of the class',
        'Maintain balanced class distribution when possible',
        'Remove ambiguous or low-quality images',
        'Use consistent image resolution across the dataset',
      ];
    case 'instance_segmentation':
      return [
        'Create precise polygon masks around each object',
        'Separate overlapping instances with distinct masks',
        'Include all visible parts of partially occluded objects',
        'Use smooth polygon boundaries, avoid jagged edges',
        'Ensure masks don\'t overlap between different instances',
      ];
    case 'semantic_segmentation':
      return [
        'Label every pixel in the image with a class',
        'Use consistent colors/IDs for each semantic class',
        'Pay attention to boundaries between classes',
        'Include an "background" or "void" class for unlabeled regions',
        'Ensure mask dimensions match image dimensions exactly',
      ];
    case 'pose_estimation':
      return [
        'Mark all visible keypoints accurately',
        'Use visibility flags: 0=not labeled, 1=occluded, 2=visible',
        'Maintain consistent keypoint ordering',
        'Include bounding box around the person/object',
        'Label all people in multi-person scenes',
      ];
    case 'action_recognition':
      return [
        'Trim videos to contain only the target action',
        'Ensure consistent frame rate across videos',
        'Include temporal boundaries for action start/end',
        'Capture actions from various angles when possible',
        'Label background/no-action segments separately',
      ];
    case 'ocr':
      return [
        'Draw tight polygons around text regions',
        'Include the transcription text for each region',
        'Handle rotated/curved text with appropriate polygons',
        'Mark illegible text separately',
        'Maintain reading order for multi-line text',
      ];
  }
};

// Get directory structure based on format and task
const getDirectoryStructure = (format: TAO6DatasetFormat, task: TAO6TaskType): string => {
  if (format === 'coco') {
    if (task === 'semantic_segmentation') {
      return `dataset/
├── images/
│   ├── train/
│   │   ├── image_001.jpg
│   │   └── ...
│   └── val/
│       └── ...
├── masks/
│   ├── train/
│   │   ├── mask_001.png
│   │   └── ...
│   └── val/
│       └── ...
└── annotations/
    ├── instances_train.json
    └── instances_val.json`;
    }
    return `dataset/
├── images/
│   ├── train/
│   │   ├── image_001.jpg
│   │   ├── image_002.jpg
│   │   └── ...
│   └── val/
│       ├── image_001.jpg
│       └── ...
└── annotations/
    ├── instances_train.json
    └── instances_val.json`;
  } else if (format === 'kitti') {
    return `dataset/
├── training/
│   ├── image_2/
│   │   ├── 000001.png
│   │   ├── 000002.png
│   │   └── ...
│   └── label_2/
│       ├── 000001.txt
│       ├── 000002.txt
│       └── ...
└── testing/
    └── image_2/
        └── ...`;
  } else {
    if (task === 'action_recognition') {
      return `dataset/
├── train/
│   ├── walking/
│   │   ├── video_001.mp4
│   │   └── ...
│   ├── running/
│   │   └── ...
│   └── jumping/
│       └── ...
├── val/
│   └── [same structure]
└── test/
    └── [same structure]`;
    }
    return `dataset/
├── train/
│   ├── class_1/
│   │   ├── image_001.jpg
│   │   └── ...
│   ├── class_2/
│   │   └── ...
│   └── class_n/
│       └── ...
├── val/
│   └── [same structure]
└── test/
    └── [same structure]`;
  }
};

const TAO6DatasetCard = ({
  dataset,
  open,
  onOpenChange,
  onSelectDataset,
}: TAO6DatasetCardProps) => {
  if (!dataset) return null;

  const handleSelect = () => {
    onSelectDataset?.(dataset);
    onOpenChange(false);
  };

  const labelingTips = getLabelingTips(dataset.task);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-blue-500/10">
                <Database className="h-8 w-8 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">{dataset.name}</DialogTitle>
                <DialogDescription className="mt-1">
                  {dataset.format.toUpperCase()} format dataset for {dataset.task.replace(/_/g, ' ')}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Task & Format Badges */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {getTaskBadge(dataset.task)}
          <Badge variant="outline" className="font-mono">
            {getFormatIcon(dataset.format)}
            <span className="ml-1">{dataset.format.toUpperCase()}</span>
          </Badge>
          <Badge variant="secondary">
            {dataset.totalSize}
          </Badge>
        </div>

        <Separator className="my-4" />

        {/* Dataset Statistics */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-blue-500" />
            Dataset Statistics
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Train</div>
                <div className="text-xl font-bold text-green-600">
                  {dataset.trainCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Validation</div>
                <div className="text-xl font-bold text-blue-600">
                  {dataset.valCount.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Test</div>
                <div className="text-xl font-bold">
                  {dataset.testCount?.toLocaleString() || '-'}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/50">
              <CardContent className="pt-3 pb-3 text-center">
                <div className="text-xs text-muted-foreground mb-1">Classes</div>
                <div className="text-xl font-bold text-purple-600">
                  {dataset.classCount}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Classes */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4 text-purple-500" />
            Classes ({dataset.classes.length})
          </h3>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {dataset.classes.map((cls, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {cls}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Directory Structure */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FolderTree className="h-4 w-4 text-yellow-500" />
            Directory Structure
          </h3>
          <Card className="bg-muted/30">
            <CardContent className="pt-3 pb-3">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre overflow-x-auto">
                {getDirectoryStructure(dataset.format, dataset.task)}
              </pre>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-4" />

        {/* Annotation Example */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FileCode className="h-4 w-4 text-green-500" />
            Annotation Example
          </h3>
          <Card className="bg-muted/30">
            <CardContent className="pt-3 pb-3">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre overflow-x-auto max-h-64">
                {getAnnotationExample(dataset.task, dataset.format)}
              </pre>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-4" />

        {/* Labeling Tips */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            Labeling Guidelines
          </h3>
          <div className="space-y-2">
            {labelingTips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Common Mistakes */}
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-orange-500" />
            Common Mistakes to Avoid
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <span>Inconsistent class naming</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <span>Missing annotations for visible objects</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <span>Incorrect file paths in JSON</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <span>Mismatched image dimensions</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          {onSelectDataset && (
            <Button onClick={handleSelect} className="bg-purple-600 hover:bg-purple-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Select Dataset
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TAO6DatasetCard;
